import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  LayoutGrid,
  ClipboardList,
  BookOpen,
  Users,
  CalendarDays,
  Wallet,
  Receipt,
  ShieldCheck,
  Banknote,
  FileText,
  Scale,
  Building2,
  HardHat,
  Settings,
  UserRound,
  Plane,
  FolderLock,
  AlertTriangle,
  CheckSquare,
  ChevronRight,
  Gauge,
  FolderOpen,
  UserCheck
} from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { PageBody } from '../../../shared/components/ui/primitives';
import { workforceHubSectionsFor } from '../../../shared/utils/workforceAccess';
import WorkforceDashboard from './WorkforceDashboard';
import { useApproverAccess } from '../../hooks/useApproverAccess';
import { getApprovalsInbox } from '../../services/api/implementation/impl';
import { ApprovalsPanel } from '../Approvals/ApprovalsInbox';
import { buildReturnToFromLocation, navigateWithReturn } from '../../../shared/navigation';

const ICON_BY_PATH = {
  '/builder/workforce': LayoutGrid,
  '/builder/operations/labour': ClipboardList,
  '/builder/operations/labour/muster': BookOpen,
  '/builder/operations/labour/gangs': Users,
  '/builder/operations/labour/approvals': CheckSquare,
  '/builder/hr/attendance': CalendarDays,
  '/builder/hr/employees': UserRound,
  '/builder/hr/leave': Plane,
  '/builder/hr/site-approvals': CheckSquare,
  '/builder/operations/labour/wages': Wallet,
  '/builder/operations/labour/spend': Receipt,
  '/builder/operations/labour/payment-approvals': ShieldCheck,
  '/builder/hr/payroll': Banknote,
  '/builder/operations/labour/registers': FileText,
  '/builder/workforce/reports': ShieldCheck,
  '/builder/operations/labour/compliance': Scale,
  '/builder/hr/policies': BookOpen,
  '/builder/hr/vault': FolderLock,
  '/builder/operations/labour/anomalies': AlertTriangle,
  '/builder/hr/office-locations': Building2,
  '/builder/operations/labour/contractors': HardHat,
  '/builder/notifications/preferences': Settings,
  '/builder/workforce/approvers': UserCheck
};

/* Group cards are drawn like the Company page's cards (the reference the
 * product picked on 28 Sep 2026): a 56px tinted square, a 20px title with a
 * grey subtitle, on a white rounded-2xl card that lifts on hover. The square
 * carries the group's icon where a company carries its initials, and the
 * colours are the same five Company rotates through. */
const GROUP_THEMES = {
  teal: 'bg-[#E7F2EF] border-[#BFE6DE] text-[#2A565E]',
  indigo: 'bg-[#EEF2FF] border-[#D6DBFF] text-[#4F46E5]',
  amber: 'bg-[#FFF4E8] border-[#FBD7B2] text-[#B45309]',
  violet: 'bg-[#F3E8FF] border-[#E2CCFF] text-[#7E22CE]',
  sky: 'bg-[#EAF6FF] border-[#C5E4FA] text-[#0369A1]'
};

const GROUP_LOOK = {
  Overview: { icon: Gauge, theme: 'teal' },
  People: { icon: Users, theme: 'indigo' },
  Attendance: { icon: CalendarDays, theme: 'sky' },
  Payroll: { icon: Banknote, theme: 'teal' },
  Records: { icon: FolderOpen, theme: 'violet' },
  Site: { icon: HardHat, theme: 'amber' },
  Muster: { icon: BookOpen, theme: 'sky' },
  Payments: { icon: Wallet, theme: 'teal' },
  Compliance: { icon: Scale, theme: 'violet' },
  Settings: { icon: Settings, theme: 'indigo' }
};

const TAB_LABEL = { Overview: 'Dashboard' };

/* Same chip as ManagementHeaderTabs, so Workforce's header matches the
 * Operations and Management dashboards. */
const tabClass = (active) =>
  `rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

/**
 * One place for labour and HR without typing a URL.
 *
 * Two tracks before anything else, because a workman and a salaried employee
 * are paid on different terms by different people. Grouping by function first
 * put the labour muster and the employee register under one heading, and a
 * builder looking for one had to read past the other.
 */
const WorkforceHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const vertical = Cookies.get('userVertical')?.toLowerCase();
  const designation = Cookies.get('userDesignation');
  const { isOwner, isApprover } = useApproverAccess();
  // Who approves what is the account owner's call alone, and owner-ness comes
  // from the server rather than cookies — so the card is added here.
  const tracks = workforceHubSectionsFor(designation, vertical).map((t) =>
    isOwner && t.track === 'Settings'
      ? {
          ...t,
          groups: t.groups.map((g) =>
            g.group === 'Settings'
              ? {
                  ...g,
                  items: [
                    ...g.items,
                    {
                      track: 'Settings',
                      group: 'Settings',
                      title: 'Approvers',
                      blurb: 'Who may give final approval, per kind of approval.',
                      path: '/builder/workforce/approvers'
                    }
                  ]
                }
              : g
          )
        }
      : t
  );
  const visiblePaths = new Set(
    tracks.flatMap((t) => t.groups.flatMap((g) => g.items.map((i) => i.path)))
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Header tabs, drawn like the Operations / Management dashboards' (same
  // green chip). One per track the person can see; Overview is labelled
  // "Dashboard" and only offered when the dashboard has a panel for them.
  const hasDashboard =
    visiblePaths.has('/builder/workforce/reports') || visiblePaths.has('/builder/hr/attendance');
  const trackTabs = tracks
    .filter((t) => t.track !== 'Overview' || hasDashboard)
    .map((t) => ({ key: t.track.toLowerCase(), label: TAB_LABEL[t.track] ?? t.track, track: t }));

  // Approvals lives here, not in the sidebar (29 Sep). Only the account owner
  // and the people he has assigned get the tab — the server decides, so an
  // approver from any vertical sees it, and nobody else does. It sits next to
  // the dashboard because it is work waiting, with the count on the chip.
  const inbox = useQuery({
    queryKey: ['approvalsInbox'],
    queryFn: getApprovalsInbox,
    enabled: isApprover,
    staleTime: 60 * 1000,
    retry: false
  });
  const waiting = Object.values(inbox.data?.meta?.counts ?? {}).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  );
  const approvalsTab = isApprover
    ? [{ key: 'approvals', label: `Approvals (${String(waiting).padStart(2, '0')})`, approvals: true }]
    : [];
  const tabs =
    trackTabs[0]?.key === 'overview'
      ? [trackTabs[0], ...approvalsTab, ...trackTabs.slice(1)]
      : [...approvalsTab, ...trackTabs];
  // Kept in the URL so Back from a screen returns to the tab it was opened from.
  const requested = searchParams.get('tab');
  const active = tabs.find((t) => t.key === requested) ?? tabs[0];
  const selectTab = (key) => setSearchParams(key === tabs[0]?.key ? {} : { tab: key });

  // No intro line: the dashboard opens the page, and the track headings below
  // already say what each half is for.
  return (
    <Layout>
      <Header data={{ name: 'Workforce' }}>
        {tabs.length > 1 && (
          <div className="flex items-center gap-2" role="tablist" aria-label="Workforce sections">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={tab.key === active?.key}
                onClick={() => selectTab(tab.key)}
                className={tabClass(tab.key === active?.key)}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </Header>
      <PageBody>
        {active?.approvals && <ApprovalsPanel />}
        {(active && !active.approvals ? [active.track] : []).map((track) => (
          <section key={track.track} className="space-y-3">
            {/* No heading row: the header tab already names the section. */}
            {/* Overview used to hold one link, "Dashboard", pointing back at
                this page. The dashboard now lives here instead. */}
            {track.track === 'Overview' ? (
              <WorkforceDashboard paths={visiblePaths} />
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {track.groups.map((group) => (
                  <GroupCard key={`${track.track}-${group.group}`} group={group}>
                    <ul className="space-y-1">
                      {group.items.map((item) => {
                        const Icon = ICON_BY_PATH[item.path] || LayoutGrid;
                        return (
                          <li key={item.path}>
                            <button
                              type="button"
                              onClick={() =>
                                // Carry the tab it was opened from, so Back on
                                // that screen returns here, not to Dashboard.
                                navigateWithReturn(navigate, item.path, {
                                  returnTo: buildReturnToFromLocation(location)
                                })
                              }
                              className="group/row flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left text-[#374151] transition-colors hover:bg-[#F7FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4D6C72]">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E5EAF1] bg-[#F7FAFB] text-[#4D6C72]">
                                <Icon className="h-4 w-4" strokeWidth={1.75} />
                              </span>
                              <span className="flex-1 truncate text-[15px] font-medium">
                                {item.title}
                              </span>
                              <ChevronRight
                                size={16}
                                className="shrink-0 text-[#CBD5E1] transition-colors group-hover/row:text-[#4D6C72]"
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </GroupCard>
                ))}
              </div>
            )}
          </section>
        ))}
      </PageBody>
    </Layout>
  );
};

const GroupCard = ({ group, children }) => {
  const look = GROUP_LOOK[group.group] || { icon: LayoutGrid, theme: 'teal' };
  const GroupIcon = look.icon;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[#E5EAF1] bg-white p-4 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#D8DEE6] hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border ${
            GROUP_THEMES[look.theme]
          }`}>
          <GroupIcon size={24} strokeWidth={1.75} />
        </div>
        <h3 className="min-w-0 truncate text-[20px] font-semibold leading-tight text-[#111827]">
          {group.group}
        </h3>
      </div>
      {children}
    </div>
  );
};

export default WorkforceHub;
